<script setup>
import { computed, onMounted, ref, watch } from "vue"
import { api, patch, post } from "../api"

const rules = ref([])
const baseOptions = ref({ users: [], connections: [] })
const dimensions = ref({ partners: [], brands: [], campaigns: [], countries: [] })
const message = ref("")
const blankForm = () => ({
  name: "", user_id: "", integration_connection_id: "", metric: "registrations_count", period: "1_day",
  comparison_type: "previous_period", operator: "greater_than", threshold: 10, change_percent: 20,
  scope_level: "integration", partner_external_id: "", brand_external_id: "", campaign_external_id: "",
  country: "", active: true
})
const form = ref(blankForm())

const needsPartner = computed(() => ["partner", "brand", "campaign"].includes(form.value.scope_level))
const needsBrand = computed(() => ["brand", "campaign"].includes(form.value.scope_level))
const needsCampaign = computed(() => form.value.scope_level === "campaign")

async function load() {
  [rules.value, baseOptions.value] = await Promise.all([api("/alert_rules"), api("/alert_rules/options")])
  if (!form.value.user_id) form.value.user_id = baseOptions.value.users[0]?.id || ""
  if (!form.value.integration_connection_id) form.value.integration_connection_id = baseOptions.value.connections[0]?.id || ""
  await loadDimensions()
}

async function loadDimensions() {
  if (!form.value.integration_connection_id) return
  dimensions.value = await api(`/alert_rules/options?integration_connection_id=${form.value.integration_connection_id}`)
}

watch(() => form.value.integration_connection_id, async () => {
  form.value.partner_external_id = ""
  form.value.brand_external_id = ""
  form.value.campaign_external_id = ""
  form.value.country = ""
  await loadDimensions()
})

watch(() => form.value.scope_level, () => {
  if (!needsPartner.value) form.value.partner_external_id = ""
  if (!needsBrand.value) form.value.brand_external_id = ""
  if (!needsCampaign.value) form.value.campaign_external_id = ""
})

async function createRule() {
  const payload = { ...form.value }
  if (payload.comparison_type === "previous_period") payload.threshold = null
  else payload.change_percent = null
  await post("/alert_rules", { alert_rule: payload })
  message.value = "Правило алерта создано"
  const recipient = form.value.user_id
  const connection = form.value.integration_connection_id
  form.value = { ...blankForm(), user_id: recipient, integration_connection_id: connection }
  await load()
}

async function toggle(rule) {
  await patch(`/alert_rules/${rule.id}`, { alert_rule: { active: !rule.active } })
  await load()
}

async function remove(rule) {
  if (!confirm(`Удалить правило «${rule.name}»?`)) return
  await api(`/alert_rules/${rule.id}`, { method: "DELETE" })
  await load()
}

function ruleSummary(rule) {
  const metric = rule.metric === "registrations_count" ? "Регистрации" : "Первые депозиты"
  const periods = { "12_hours": "12 ч", "1_day": "сутки", "3_days": "3 дня", "7_days": "7 дней" }
  const condition = rule.comparison_type === "fixed_threshold"
    ? `${rule.operator === "greater_than" ? ">" : "<"} ${Number(rule.threshold)}`
    : `${rule.operator === "greater_than" ? "рост" : "падение"} на ${Number(rule.change_percent)}%`
  return `${metric} · ${periods[rule.period]} · ${condition}`
}

onMounted(load)
</script>

<template>
  <div class="panel alert-settings">
    <div class="alert-settings-heading">
      <div><h2>Алерты метрик</h2><p>Отдельные уведомления о срабатывании и возврате метрики в норму.</p></div>
      <span>{{ rules.length }} правил</span>
    </div>
    <div v-if="message" class="notice">{{ message }}</div>
    <form class="alert-rule-form" @submit.prevent="createRule">
      <label class="wide">Название<input v-model="form.name" placeholder="Например, падение FTD по GE" required></label>
      <label>Получатель<select v-model="form.user_id" required><option v-for="user in baseOptions.users" :key="user.id" :value="user.id">{{ user.name }}{{ user.telegram_configured ? "" : " · нет chat ID" }}</option></select></label>
      <label>Интеграция<select v-model="form.integration_connection_id" required><option v-for="item in baseOptions.connections" :key="item.id" :value="item.id">{{ item.name }}{{ item.active ? "" : " · выключена" }}</option></select></label>
      <label>Метрика<select v-model="form.metric"><option value="registrations_count">Регистрации</option><option value="first_deposits_count">Первые депозиты</option></select></label>
      <label>Период<select v-model="form.period"><option value="12_hours">12 часов</option><option value="1_day">Сутки</option><option value="3_days">3 дня</option><option value="7_days">7 дней</option></select></label>
      <label>Сравнение<select v-model="form.comparison_type"><option value="previous_period">С предыдущим периодом</option><option value="fixed_threshold">С фиксированным порогом</option></select></label>
      <label>Условие<select v-model="form.operator"><option value="greater_than">Стало больше</option><option value="less_than">Стало меньше</option></select></label>
      <label v-if="form.comparison_type === 'previous_period'">Изменение, %<input v-model.number="form.change_percent" min="0" step="1" type="number" required></label>
      <label v-else>Порог<input v-model.number="form.threshold" min="0" step="1" type="number" required></label>
      <label>Уровень<select v-model="form.scope_level"><option value="integration">Интеграция</option><option value="partner">+ партнёр</option><option value="brand">+ бренд</option><option value="campaign">+ кампания</option></select></label>
      <label v-if="needsPartner">Партнёр<select v-model="form.partner_external_id" required><option value="" disabled>Выберите партнёра</option><option v-for="item in dimensions.partners" :key="item.id" :value="item.id">{{ item.name }} · #{{ item.id }}</option></select></label>
      <label v-if="needsBrand">Бренд<select v-model="form.brand_external_id" required><option value="" disabled>Выберите бренд</option><option v-for="id in dimensions.brands" :key="id" :value="id">#{{ id }}</option></select></label>
      <label v-if="needsCampaign">Кампания<select v-model="form.campaign_external_id" required><option value="" disabled>Выберите кампанию</option><option v-for="id in dimensions.campaigns" :key="id" :value="id">#{{ id }}</option></select></label>
      <label>GEO<select v-model="form.country"><option value="">Все GEO отдельно</option><option v-for="country in dimensions.countries" :key="country" :value="country">{{ country }}</option></select></label>
      <button class="primary wide">Создать правило</button>
    </form>
    <div class="alert-rule-list">
      <div v-for="rule in rules" :key="rule.id" class="alert-rule-row">
        <span class="alert-rule-indicator" :class="{ inactive: !rule.active }"></span>
        <div><strong>{{ rule.name }}</strong><small>{{ ruleSummary(rule) }} · {{ rule.integration_name }} · {{ rule.country || "все GEO" }}</small><small>{{ rule.user_name }}{{ rule.telegram_configured ? "" : " · Telegram chat ID не задан" }}</small></div>
        <button @click="toggle(rule)">{{ rule.active ? "Отключить" : "Включить" }}</button>
        <button class="danger" @click="remove(rule)">Удалить</button>
      </div>
      <p v-if="!rules.length" class="empty-alerts">Правил пока нет.</p>
    </div>
  </div>
</template>
